import PropTypes from "prop-types";
import React from "react";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Stepper from "react-stepper-horizontal";
import { reduxForm, Field } from "redux-form";
import * as constants from "components/WebOrder/WebOrderConstants";
import { cardExpirationMonths } from "components/WebOrder/DropdownLists";
import { MSpinner, ReadOnlyField } from "components/Shared";
import logo from "static/images/logo.png";
import {
  CheckboxField, InputField, MSelectField,
  normalizeCreditCardNumber, normalizeCreditCardCVC
} from "components/FormElements";
import { validateCheckout } from "validations/checkout";
import { stepperThemeProps } from "components/Shared/MStepper";


class Checkout extends React.Component {
  constructor(props) {
    super(props);
    this.displayName = "Checkout";

    this.state = {
      formIsSubmitting: false,
      ConfirmedFormSubmit: false,

      cardNumber: "",
      cardName: "",
      cardExpirationMonth: "",
      cardExpirationYear: "",
      cardCVC: "",
      cardFieldFocused: "", //this is the field that is highlighted on the fancy credit card picture to show what youre editing

      dialog: {
        actions: null,
        visible: false,
        title: "",
        text: ""
      },

      borrowerEmail: "",

      activeStep: 0,
      stepTransitions: [true, false, false, false],
      redirect: false,
      blocking: false,
      blockRef: this.props.history.block((location) => this.navBlock(location)),
    };
    this.stepTransition = this.stepTransition.bind(this);
    this.getStateChangedFunction = this.getStateChangedFunction.bind(this);
    this.getCardExpirationYears = this.getCardExpirationYears.bind(this);
    this.creditCardPropertyFocused = this.creditCardPropertyFocused.bind(this);
    this.creditCardFieldBlurred = this.creditCardFieldBlurred.bind(this);

    this.makeToast = this.makeToast.bind(this);
    this.getError = this.getError.bind(this);

    this.submitFinished = this.submitFinished.bind(this);
    this.customSubmit = this.customSubmit.bind(this);

    this.showCancelDialog = this.showCancelDialog.bind(this);
    this.showConfirmDialog = this.showConfirmDialog.bind(this);
    this.showSubmittingDialog = this.showSubmittingDialog.bind(this);
    this.showDialog = this.showDialog.bind(this);
    this.hideDialog = this.hideDialog.bind(this);
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.closeBlock);
  }

  componentWillUnmount() {
    this.state.blockRef();
    window.removeEventListener("beforeunload", this.closeBlock);
  }
  
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.confirmationNumber) {
      this.props.pushToConfirmationPage();
    }
  }

  getCardExpirationYears() {
    const cardExpirationYears = [];
    for (let i = 0; i <= 20; i++) {
      cardExpirationYears.push((new Date()).getFullYear() + i);
    }
    return cardExpirationYears;
  }
  getStateChangedFunction(stateVariable) { return (event, newValue) => { this.setState({ [stateVariable]: newValue }); }; }
  creditCardPropertyFocused(cardField) { return () => this.setState({ cardFieldFocused: cardField }); }
  creditCardFieldBlurred() { this.setState({ cardFieldFocused: "" }); }

  // ------------------------------------------------------------------------------------------------
  // Toast and Error message helpers
  makeToast(text, type) { this.props.makeToast(text, type); }

  //simple method that will cause a controls error text to show if the variable passed in is not null or empty
  //pass true for meta if this is going to be used on an InputField because it is a custom control that handles errors different
  getError(message, meta) {
    if (message) {
      return meta
        ? { meta: { error: message, touched: true } }
        : { error: true, errorText: message };
    }
  }


  // ------------------------------------------------------------------------------------------------
  // Dialog methods
  showCancelDialog() {
    const actions = [];
    actions.push(<Button key="cancel" variant="text" color="secondary" onClick={this.hideDialog}>Cancel</Button>);
    actions.push(<Button key="confirm" variant="text" onClick={() => { this.setState({ redirect: true }); }}>Confirm</Button>);
    this.showDialog("Cancel Order", `Are you sure?`, actions);
  }

  showConfirmDialog() {
    const actions = [];
    actions.push(<Button key="cancel" variant="text" color="secondary" onClick={this.hideDialog}>Cancel</Button>);
    actions.push(<Button key="confirm" variant="text" onClick={(e) => { this.hideDialog(); this.customSubmit(e); }}>Confirm</Button>);
    this.showDialog("Place Order", `Are you sure?`, actions);
  }

  showSubmittingDialog(title, text, actions) {
    this.setState({ dialog: { actions: actions, visible: true, title: title, text: text } });
  }

  showDialog(title, text, actions) {
    if (typeof actions === "string") {
      actions = [
        <Button key="action" onClick={this.hideDialog}>
          {actions}
        </Button>
      ];
    }
    this.setState({ dialog: { actions: actions, visible: true, title: title, text: text } });
  }

  hideDialog() { this.setState({ dialog: { actions: null, visible: false, title: "", text: "" } }); }

  // ------------------------------------------------------------------------------------------------
  // Submit handling methods
  //we need this because it was requested that if the form validation fails we show a snack message that says that it failed
  customSubmit(proxy, event) {
    this.setState({ formIsSubmitting: true, redirect: true });
    this.showSubmittingDialog("Submitting", <MSpinner />, [<Button key="close" variant="text" color="secondary" onClick={this.hideDialog}>Close</Button>]);

    let results = this.props.handleSubmit(
      orderData => this.props.saveCheckout(this.props.token, orderData, this.submitFinished)
    )(proxy, event);


    if (results) {
      this.setState({ formIsSubmitting: false, redirect: false });
      this.hideDialog();
      this.makeToast(constants.submitFailedMessage);
    }
    return results;
  }

  submitFinished(success) {
    this.hideDialog();
    if (!success) {
      this.setState({ formIsSubmitting: false, redirect: false });
    }
  }

  // ------------------------------------------------------------------------------------------------
  // Step handling 
  stepTransition(next) {
    let nextStep = this.state.activeStep;

    if (next) {
      this.setState({ formIsSubmitting: true });

      if (nextStep === constants._CHECKOUTLOAN) {
        // nothing to do here
        nextStep++;
      } else {
        let results = this.props.handleSubmit(
          orderData => this.props.saveCheckout(this.props.token, orderData, null)
        )();
        if (results) {
          this.makeToast(constants.submitFailedMessage);
        } else {
          nextStep++;
        }
      }
    } else {
      nextStep--;
    }

    this.setState({ formIsSubmitting: false });
    if (nextStep !== this.state.activeStep) {
      let steps = [...this.state.stepTransitions];
      steps.forEach((item, index) => {
        if (index === nextStep)
          steps[index] = true;
        else steps[index] = false;
      });

      this.setState({ activeStep: nextStep, stepTransitions: steps });
    }
  }

  navBlock(location) {
    if (!this.state.redirect) {
      if (location.pathname !== this.props.location.pathname) {
        return "Are you sure you want to leave this page?";
      }
    }
  }

  closeBlock(e) {
    if (!this.state.redirect) {
      let confirmationMessage = "\o/";
      (e || window.event).returnValue = confirmationMessage;
      return confirmationMessage;
    } else return null;
  }

  render() {
    const {
      checkout,
    } = this.props;

    // we are using _LOAN and _PAYMENT steps in Checkout
    let OrderSteps = () => {
      let hasError = (checkout && checkout.result && checkout.result.error);

      if (hasError) {
        return (
            <div className="waiver-form">
              <img src={logo} alt="logo.png" className="mueller-logo" />
              <h3 className="mueller-title" data-test="title">Borrower Payment Request</h3>
              <div>
                <div className="waiver-error">
                  {checkout.result.errorMessages}
                </div>
                <br />
                <div>
                  If you have any questions regarding this order please contact Mueller Customer Service at customerservice@muellerreports.com.
                </div>
              </div>
            </div>
        );
      }

      return (
        <div className="order-step-buttons">
          <hr />
          <Button
            secondary
            raised
            onClick={() => this.stepTransition(false)}
            children="Previous"
            className="m-t-10 save previous"
            disabled={this.state.activeStep === constants._CHECKOUTLOAN}
          />
          {
            this.state.activeStep !== constants._CHECKOUTPAYMENT &&
            <Button
              primary
              raised
              onClick={() => this.stepTransition(true)}
              children="Next"
              className="m-t-10 save next"
              style={{ right: 0 }}
              disabled={hasError}
            />
          }
          {
            this.state.activeStep === constants._CHECKOUTPAYMENT &&
            <div>
              <div style={{ width: "100%", textAlign: "right" }} >
                <Button
                  primary
                  raised
                  style={{ marginLeft: "5px" }}
                  children="Place Order"
                  className="m-t-10 save"
                  data-test="Place-Order"
                  disabled={hasError || this.state.formIsSubmitting}
                  onClick={this.showConfirmDialog}
                />
              </div>
            </div>
          }
        </div>
      );
    };

    return (
      <div className="web-order" style={{ width: "100%" }}>
        <div className="order-steppers">
          <Stepper
            steps={[
              { title: "Loan Information Review" },
              { title: "Payment Information" }
            ]}
            activeStep={this.state.activeStep}
            {...stepperThemeProps}
          />
        </div>
        <form onSubmit={this.customSubmit} ref={form => { this.target = form; }}>
          <Dialog
            open={this.state.dialog.visible}
            id="dialog"
            aria-describedby="dialogParagraph"
          >
            <DialogTitle>{this.state.dialog.title}</DialogTitle>
            <DialogContent>
              <p id="dialogParagraph" className="md-color--secondary-text">
                {this.state.dialog.text}
              </p>
            </DialogContent>
            <DialogActions>
              {this.state.dialog.actions}
            </DialogActions>
          </Dialog>
          {
            this.state.stepTransitions[constants._CHECKOUTLOAN] &&
            <Card className="step-cards">
              <CardContent>
                <div className="md-grid">
                  <div className="md-cell md-cell--11  md-cell--1-offset" >
                    <h4><b>Loan order information</b></h4>
                  </div>
                  <div className="md-cell md-cell--5 md-cell--1-offset" >
                    <ReadOnlyField title="Lender" fieldValue={checkout.lenderName} />
                    <ReadOnlyField title="Loan Number" fieldValue={checkout.loanNumber} />
                    <ReadOnlyField title="Report Type" fieldValue={checkout.reportName} />
                    <ReadOnlyField title="Mueller Control #" fieldValue={checkout.controlNumber + ""} />
                  </div>
                  <div className="md-cell md-cell--5 md-cell--1-offset" >
                    <ReadOnlyField title="Borrower Name" fieldValue={checkout.borrowerName} />
                    <ReadOnlyField title="Property Address" fieldValue={checkout.propertyAddress} />
                    <ReadOnlyField title="City, State Zip" fieldValue={checkout.propertyCity + ", " + checkout.propertyState + " " + checkout.propertyZip} />
                  </div>
                  <OrderSteps />
                </div>
              </CardContent>
            </Card>
          }
          {
            this.state.stepTransitions[constants._CHECKOUTPAYMENT] &&
            <Card className="step-cards">
              <CardContent>
                <div className="md-grid">
                  <div className="md-grid md-cell md-cell--12">                    
                    <div className="md-cell md-cell--12" >
                      <div style={{margin: "10px"}}>An Appraisal has been ordered on your behalf. You are responsible for providing payment before the order can be scheduled for completion.</div>                    
                      <div className="explain">{constants.ExplainPayNow}</div>
                    </div>
                    <Field
                      containerClassName="md-cell md-cell--6"
                      id="cardNumber"
                      name="cardNumber"
                      //required
                      component={InputField}
                      normalize={normalizeCreditCardNumber}
                      type="text"
                      hintText="Card Number"
                      dataTest="card-number"
                      onChange={this.getStateChangedFunction("cardNumber")}
                      onFocus={this.creditCardPropertyFocused("number")}
                      onBlur={this.creditCardFieldBlurred}
                    />
                    <Field
                      containerClassName="md-cell md-cell--6"
                      id="cardName"
                      name="cardName"
                      //required
                      component={InputField}
                      type="text"
                      hintText="Cardholder Name"
                      dataTest="card-name"
                      onChange={this.getStateChangedFunction("cardName")}
                      onFocus={this.creditCardPropertyFocused("name")}
                      onBlur={this.creditCardFieldBlurred}
                    />
                    <Field
                      containerClassName="md-cell md-cell--4"
                      id="cardExpirationMonth"
                      name="cardExpirationMonth"
                      //required
                      component={MSelectField}
                      label="Expiration Month"
                      itemLabel="label"
                      itemValue="label"
                      menuItems={cardExpirationMonths}
                      onChange={this.getStateChangedFunction("cardExpirationMonth")}
                      onFocus={this.creditCardPropertyFocused("expiry")}
                      onBlur={this.creditCardFieldBlurred}
                    />
                    <Field
                      containerClassName="md-cell md-cell--4"
                      id="cardExpirationYear"
                      name="cardExpirationYear"
                      //required
                      component={MSelectField}
                      label="Expiration Year"
                      itemLabel="label"
                      itemValue="label"
                      menuItems={this.getCardExpirationYears()}
                      onChange={this.getStateChangedFunction("cardExpirationYear")}
                      onFocus={this.creditCardPropertyFocused("expiry")}
                      onBlur={this.creditCardFieldBlurred}
                    />
                    <Field
                      containerClassName="md-cell md-cell--4"
                      id="cardCVC"
                      name="cardCVC"
                      //required
                      component={InputField}
                      normalize={normalizeCreditCardCVC}
                      type="text"
                      hintText="CVC"
                      dataTest="card-cvc"
                      onChange={this.getStateChangedFunction("cardCVC")}
                      onFocus={this.creditCardPropertyFocused("cvc")}
                      onBlur={this.creditCardFieldBlurred}
                    />
                    <div className="authReporting" style={{ textAlign: "left", marginTop: "20px" }}>
                      <div className="md-grid">
                        <div className="md-cell md-cell--12" style={{ fontWeight: "bold", color: "#C8102E", fontSize: 14 }}>
                          Billing Amount: ${parseFloat(this.props.checkout.billingAmount).toFixed(2)}
                        </div>
                        <Field
                          containerClassName="md-cell md-cell--12"
                          id="authBillingAmount"
                          name="authBillingAmount"
                          type="checkbox"
                          component={CheckboxField}
                          label="I authorize a hold to be placed on the card provided for the amount listed above"
                        />
                      </div>
                    </div>
                  </div>
                  <OrderSteps />
                </div>
              </CardContent>
            </Card>
          }
        </form>
      </div>
    );
  }
}

Checkout.propTypes = {
  token: PropTypes.string.isRequired,
  savingCheckout: PropTypes.bool.isRequired,
  cancelingCheckout: PropTypes.bool.isRequired,
  checkout: PropTypes.object.isRequired,
  saveCheckout: PropTypes.func.isRequired,
  cancelCheckout: PropTypes.func.isRequired,

  makeToast: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pushToConfirmationPage: PropTypes.func.isRequired,
  confirmationNumber: PropTypes.string,

  history: PropTypes.object,
  location: PropTypes.object,
  initialize: PropTypes.func,
  dispatch: PropTypes.func,
  change: PropTypes.func,
};

export default (reduxForm({
  form: "Checkout",
  validate: validateCheckout
}))(Checkout);
